import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: async (email, password) => {
    // Send JSON body to the backend json login endpoint
    const response = await api.post('/auth/login/json', { email, password });
    return response.data;
  },
  
  loginJson: async (email, password) => {
    const response = await api.post('/auth/login/json', { email, password });
    return response.data;
  },
  
  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
  
  getProfile: async () => {
    const response = await api.get('/auth/me/profile');
    return response.data;
  },

  changePassword: async (currentPassword, newPassword) => {
    const response = await api.put('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    });
    return response.data;
  },
};

// Student APIs
export const studentAPI = {
  getProfile: async () => {
    const response = await api.get('/student/profile');
    return response.data;
  },
  
  getResults: async (academicYear, subject) => {
    const params = new URLSearchParams();
    if (academicYear) params.append('academic_year', academicYear);
    if (subject) params.append('subject', subject);
    
    const response = await api.get(`/student/results?${params}`);
    return response.data;
  },
  
  getResultsSummary: async (academicYear) => {
    const params = academicYear ? `?academic_year=${academicYear}` : '';
    const response = await api.get(`/student/results/summary${params}`);
    return response.data;
  },
  
  getFees: async () => {
    const response = await api.get('/student/fees');
    return response.data;
  },
  
  getFeeDetail: async (feeId) => {
    const response = await api.get(`/student/fees/${feeId}`);
    return response.data;
  },
};

// Admin APIs
export const adminAPI = {
  // Students
  createStudent: async (data) => {
    const response = await api.post('/admin/students', data);
    return response.data;
  },
  
  getStudents: async (grade, section, search) => {
    const params = new URLSearchParams();
    if (grade) params.append('grade', grade);
    if (section) params.append('section', section);
    if (search) params.append('search', search);
    
    const response = await api.get(`/admin/students?${params}`);
    return response.data;
  },
  
  getStudent: async (id) => {
    const response = await api.get(`/admin/students/${id}`);
    return response.data;
  },
  
  updateStudent: async (id, data) => {
    const response = await api.put(`/admin/students/${id}`, data);
    return response.data;
  },
  
  deleteStudent: async (id) => {
    await api.delete(`/admin/students/${id}`);
  },
  
  // Faculty
  createFaculty: async (data) => {
    const response = await api.post('/admin/faculty', data);
    return response.data;
  },
  
  getFaculty: async (department, subject) => {
    const params = new URLSearchParams();
    if (department) params.append('department', department);
    if (subject) params.append('subject', subject);
    
    const response = await api.get(`/admin/faculty?${params}`);
    return response.data;
  },
  
  updateFaculty: async (id, data) => {
    const response = await api.put(`/admin/faculty/${id}`, data);
    return response.data;
  },
  
  deleteFaculty: async (id) => {
    await api.delete(`/admin/faculty/${id}`);
  },
  
  // Results
  createResult: async (data) => {
    const response = await api.post('/admin/results', data);
    return response.data;
  },
  
  getResults: async (studentId, subject, academicYear) => {
    const params = new URLSearchParams();
    if (studentId) params.append('student_id', studentId);
    if (subject) params.append('subject', subject);
    if (academicYear) params.append('academic_year', academicYear);
    
    const response = await api.get(`/admin/results?${params}`);
    return response.data;
  },
  
  updateResult: async (id, data) => {
    const response = await api.put(`/admin/results/${id}`, data);
    return response.data;
  },
  
  deleteResult: async (id) => {
    await api.delete(`/admin/results/${id}`);
  },
  
  // Fees
  createFee: async (data) => {
    const response = await api.post('/admin/fees', data);
    return response.data;
  },
  
  getFees: async (studentId, paymentStatus, academicYear) => {
    const params = new URLSearchParams();
    if (studentId) params.append('student_id', studentId);
    if (paymentStatus) params.append('payment_status', paymentStatus);
    if (academicYear) params.append('academic_year', academicYear);
    
    const response = await api.get(`/admin/fees?${params}`);
    return response.data;
  },
  
  updateFee: async (id, data) => {
    const response = await api.put(`/admin/fees/${id}`, data);
    return response.data;
  },
  
  recordPayment: async (id, data) => {
    const response = await api.post(`/admin/fees/${id}/payment`, data);
    return response.data;
  },
  
  deleteFee: async (id) => {
    await api.delete(`/admin/fees/${id}`);
  },
  
  // Dashboard
  getDashboardStats: async () => {
    const response = await api.get('/admin/dashboard/stats');
    return response.data;
  },
  
  // Enrollments
  getEnrollments: async (status, applyingForClass) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (applyingForClass) params.append('applying_for_class', applyingForClass);
    
    const response = await api.get(`/admin/enrollments?${params}`);
    return response.data;
  },
  
  getEnrollment: async (id) => {
    const response = await api.get(`/admin/enrollments/${id}`);
    return response.data;
  },
  
  updateEnrollment: async (id, data) => {
    const response = await api.put(`/admin/enrollments/${id}`, data);
    return response.data;
  },
  
  deleteEnrollment: async (id) => {
    await api.delete(`/admin/enrollments/${id}`);
  },
};

// Faculty APIs
export const facultyAPI = {
  getProfile: async () => {
    const response = await api.get('/faculty/profile');
    return response.data;
  },
  
  getStudents: async (grade, section, search) => {
    const params = new URLSearchParams();
    if (grade) params.append('grade', grade);
    if (section) params.append('section', section);
    if (search) params.append('search', search);
    
    const response = await api.get(`/faculty/students?${params}`);
    return response.data;
  },
  
  addResult: async (data) => {
    const response = await api.post('/faculty/results', data);
    return response.data;
  },
  
  getResults: async (studentId, subject, academicYear) => {
    const params = new URLSearchParams();
    if (studentId) params.append('student_id', studentId);
    if (subject) params.append('subject', subject);
    if (academicYear) params.append('academic_year', academicYear);
    
    const response = await api.get(`/faculty/results?${params}`);
    return response.data;
  },
  
  updateResult: async (id, data) => {
    const response = await api.put(`/faculty/results/${id}`, data);
    return response.data;
  },
  
  deleteResult: async (id) => {
    await api.delete(`/faculty/results/${id}`);
  },
  
  bulkAddResults: async (data) => {
    const response = await api.post('/faculty/results/bulk', data);
    return response.data;
  },
  
  getStudentResults: async (studentId, subject, academicYear) => {
    const params = new URLSearchParams();
    if (subject) params.append('subject', subject);
    if (academicYear) params.append('academic_year', academicYear);
    
    const response = await api.get(`/faculty/results/student/${studentId}?${params}`);
    return response.data;
  },
};

// Enrollment APIs (Public)
export const enrollmentAPI = {
  submitApplication: async (data) => {
    const response = await api.post('/enrollment/apply', data);
    return response.data;
  },
  
  checkStatus: async (enrollmentId) => {
    const response = await api.get(`/enrollment/status/${enrollmentId}`);
    return response.data;
  },
};

export default api;
